import type { ModelCostsResponse, OwedReport, MapResponse, VoiceMode } from "@dm/shared";
import type { ScheduleResponse, ScheduleRuleInput, ScheduleSettings, UpcomingResponse } from "@dm/shared";
import type { CampaignImage, CreateImageInput, ImagesResponse, SceneMood } from "@dm/shared";
import type {
  Character,
  CharacterSheet,
  CombatResponse,
  CombatState,
  DiceRoll,
  DmActionResult,
  HpChangeKind,
  MeResponse,
  MyCharactersResponse,
  PartyResponse,
  RollInput,
  RollRequest,
  SheetPrefs,
  TranscriptResponse,
  UserPreferences,
} from "@dm/shared";
import type {
  Account,
  Campaign,
  CampaignDetailResponse,
  CampaignSettingsInput,
  CampaignsResponse,
  CharacterDetails,
  Invite,
  LiveState,
  VoiceChannel,
} from "@dm/shared";
import type {
  CampaignBible,
  GameSession,
  GlobalMemoryCategory,
  GlobalMemoryEntry,
  GlobalMemorySuggestion,
  Memory,
  MemoryInput,
  MemoryResponse,
  SessionsResponse,
  TranscriptLine,
} from "@dm/shared";
import type { Difficulty, HostPanelResponse, HostRuling, JournalEntry, RewindResult, Whisper } from "@dm/shared";

import type { HelpAnswer, HelpAskInput } from "@dm/shared";
import type { FeedbackResponse, LatencySettings, PlaytestResponse, PlaytestStatus, ReplayNote, ReplayResponse } from "@dm/shared";
import type { Adventure, AdventureContent, AdventuresResponse, BuildAdventureInput, CampaignAdventureResponse } from "@dm/shared";
import type { CharacterImport, ElevenVoice, VoiceSettingsInput } from "@dm/shared";
import type { AdminHomebrewResponse, CopyDmSource, Homebrew, HomebrewInput, HomebrewResponse, WikiIndex, WikiPage } from "@dm/shared";
import type { Appeal, LevelUpInput, LootChoice, LootResponse, MyTableSettings } from "@dm/shared";
import type { AiPrice, PriceSyncStatus, ApiKeyStatus, AuthOptions, BotStatus, CampaignCostsResponse, KeyProvider, MyKeysResponse, SessionCost, UpdateStatus } from "@dm/shared";

type GlobalMemoryResponse = { entries: GlobalMemoryEntry[]; suggestions: GlobalMemorySuggestion[] };

/** Partial character update: top-level fields replace, `details` keys replace one level deep. */
export type SheetPatch = Partial<Omit<CharacterSheet, "details">> & { details?: Partial<CharacterDetails> };

export class NotLoggedInError extends Error {}

/** Error with the server's message (e.g. "Only the campaign host can do that"). */
export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401) throw new NotLoggedInError();
  if (!res.ok) {
    let msg = `${url} -> ${res.status}`;
    try {
      const j = (await res.json()) as { message?: string; error?: string };
      msg = j.message ?? j.error ?? msg;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

const getJson = <T>(url: string) => request<T>("GET", url);
const post = <T>(url: string, body: unknown = {}) => request<T>("POST", url, body);

type CombatResult = { result: DmActionResult | null; combat: CombatState | null };

const del = <T>(url: string) => request<T>("DELETE", url);

const put = <T>(url: string, body: unknown = {}) => request<T>("PUT", url, body);
const patchJ = <T>(url: string, body: unknown = {}) => request<T>("PATCH", url, body);

/** Phase 7: the battle map. */
export const mapApi = {
  get: (c: number) => getJson<MapResponse>(`/api/campaigns/${c}/map`),
  create: (c: number, b: { name: string; cols: number; rows: number }) => post<MapResponse>(`/api/campaigns/${c}/maps`, b),
  update: (c: number, m: number, b: { name?: string; cols?: number; rows?: number; active?: boolean; fog?: boolean }) => patchJ<MapResponse>(`/api/campaigns/${c}/maps/${m}`, b),
  remove: (c: number, m: number) => del<MapResponse>(`/api/campaigns/${c}/maps/${m}`),
  fog: (c: number, m: number, mode: "reveal" | "hide" | "revealAll" | "coverAll", rect?: [number, number, number, number]) => post<MapResponse>(`/api/campaigns/${c}/maps/${m}/fog`, { mode, rect }),
  walls: (c: number, m: number, mode: "paint" | "erase" | "clear", rect?: [number, number, number, number]) => post<MapResponse>(`/api/campaigns/${c}/maps/${m}/walls`, { mode, rect }),
  upload: (c: number, m: number, dataUrl: string, name: string) => post<MapResponse>(`/api/campaigns/${c}/maps/${m}/upload`, { dataUrl, name }),
  generate: (c: number, m: number, prompt: string) => post<MapResponse>(`/api/campaigns/${c}/maps/${m}/generate`, { prompt }),
  clearImage: (c: number, m: number) => put<MapResponse>(`/api/campaigns/${c}/maps/${m}/image`, { imageId: null }),
  move: (c: number, t: number, b: { x?: number; y?: number; hidden?: boolean; size?: number }) => patchJ<MapResponse>(`/api/campaigns/${c}/map/tokens/${t}`, b),
  addToken: (c: number, b: { name: string; size?: number; hidden?: boolean; x?: number; y?: number }) => post<MapResponse>(`/api/campaigns/${c}/map/tokens`, b),
  removeToken: (c: number, t: number) => del<MapResponse>(`/api/campaigns/${c}/map/tokens/${t}`),
};

export const api = {
  // Phase 4: keys, costs, updates, bot, email login
  authOptions: () => getJson<AuthOptions>("/api/auth/options"),
  emailLoginStart: (email: string, invite?: string) => post<{ ok: true; message: string }>("/auth/email/start", { email, invite }),
  addEmail: (email: string) => post<{ ok: true }>("/api/me/email", { email }),
  removeEmail: () => del<{ ok: true }>("/api/me/email"),
  myKeys: () => getJson<MyKeysResponse>("/api/me/keys"),
  saveKey: (p: KeyProvider, key: string) => put<ApiKeyStatus>(`/api/me/keys/${p}`, { key }),
  deleteKey: (p: KeyProvider) => del<{ ok: true }>(`/api/me/keys/${p}`),
  testKey: (p: KeyProvider) => post<ApiKeyStatus>(`/api/me/keys/${p}/test`),
  setServerKey: (c: number, on: boolean) => put<{ ok: true; useServerKey: boolean; serverKeyConfigured: boolean }>(`/api/admin/campaigns/${c}/server-key`, { on }),
  prices: () => getJson<{ prices: AiPrice[] }>("/api/admin/prices"),
  savePrice: (p: { provider: string; model: string; kind: AiPrice["kind"]; usdPerMillion: number | null; source?: string }) => put<{ prices: AiPrice[] }>("/api/admin/prices", p),
  restoreDefaultPrices: () => post<{ prices: AiPrice[]; restored: number }>("/api/admin/prices/restore-defaults"),
  lockPrice: (id: number, locked: boolean) => put<{ prices: AiPrice[] }>(`/api/admin/prices/${id}/lock`, { locked }),
  priceSync: () => getJson<PriceSyncStatus>("/api/admin/prices/sync"),
  checkPricesNow: () => post<{ result: string; status: PriceSyncStatus; prices: AiPrice[] }>("/api/admin/prices/check-now"),
  deletePrice: (id: number) => del<{ prices: AiPrice[] }>(`/api/admin/prices/${id}`),
  costs: (c: number) => getJson<CampaignCostsResponse>(`/api/campaigns/${c}/costs`),
  sessionCost: (c: number, sid: number) => getJson<SessionCost>(`/api/campaigns/${c}/sessions/${sid}/cost`),
  setCostMultiplier: (c: number, multiplier: number) => put<{ multiplier: number }>(`/api/campaigns/${c}/cost-multiplier`, { multiplier }),
  setCap: (c: number, capUsd: number | null) => put<{ ok: true }>(`/api/campaigns/${c}/cap`, { capUsd }),
  updateStatus: () => getJson<UpdateStatus>("/api/update/status"),
  updateNow: () => post<UpdateStatus>("/api/admin/update/now"),
  updateCheck: () => post<UpdateStatus>("/api/admin/update/check"),
  updateCancel: () => post<UpdateStatus>("/api/admin/update/cancel"),
  botStatus: () => getJson<BotStatus>("/api/admin/bot"),

  // campaigns & accounts (Phase 2.5)
  campaigns: () => getJson<CampaignsResponse>("/api/campaigns"),
  createCampaign: (name: string, description: string) => post<Campaign>("/api/campaigns", { name, description }),
  campaignDetail: (c: number) => getJson<CampaignDetailResponse>(`/api/campaigns/${c}`),
  updateCampaign: (c: number, s: CampaignSettingsInput) => request<Campaign>("PATCH", `/api/campaigns/${c}`, s),
  selectCampaign: (c: number) => post<{ ok: true }>(`/api/campaigns/${c}/select`),
  deleteCampaign: (c: number, confirmName: string) => post<{ ok: true }>(`/api/campaigns/${c}/delete`, { confirmName }),
  restoreCampaign: (c: number) => post<Campaign>(`/api/campaigns/${c}/restore`),
  leaveCampaign: (c: number) => post<{ ok: true }>(`/api/campaigns/${c}/leave`),
  setMemberRole: (c: number, userId: string, role: "cohost" | "player") =>
    request<{ ok: true }>("PUT", `/api/campaigns/${c}/members/${userId}/role`, { role }),
  removeMember: (c: number, userId: string) => del<{ ok: true }>(`/api/campaigns/${c}/members/${userId}`),
  createInvite: (c: number) => post<Invite>(`/api/campaigns/${c}/invites`),
  revokeInvite: (c: number, token: string) => del<{ ok: true }>(`/api/campaigns/${c}/invites/${encodeURIComponent(token)}`),
  previewInvite: (token: string) => getJson<{ campaign: Campaign; alreadyMember: boolean }>(`/api/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string) => post<Campaign>(`/api/invites/${encodeURIComponent(token)}/accept`),
  requestJoin: (c: number, message: string) => post<{ ok: true }>(`/api/campaigns/${c}/join-requests`, { message }),
  decideJoin: (c: number, rid: number, approve: boolean) => post<{ ok: true }>(`/api/campaigns/${c}/join-requests/${rid}`, { approve }),
  live: (c: number) => getJson<LiveState>(`/api/campaigns/${c}/live`),
  claimTonight: (c: number) => post<{ live: LiveState; message: string }>(`/api/campaigns/${c}/live/claim`),
  takeOver: (c: number) => post<LiveState>(`/api/campaigns/${c}/live/take-over`),
  setVoiceMode: (c: number, userId: string, mode: VoiceMode) => request<LiveState>("PUT", `/api/campaigns/${c}/members/${userId}/voice-mode`, { mode }),
  dismissVoiceNotice: (c: number, id: string) => post<LiveState>(`/api/campaigns/${c}/live/notices/${encodeURIComponent(id)}/dismiss`),
  voiceChannels: () => getJson<{ channels: VoiceChannel[] }>("/api/discord/voice-channels"),
  setSharedTable: (channelId: string, shared: boolean) => request<{ ok: true; shared: boolean }>("PUT", `/api/discord/voice-channels/${channelId}/shared`, { shared }),
  accounts: () => getJson<{ accounts: Account[] }>("/api/admin/accounts"),
  owed: () => getJson<OwedReport>("/api/admin/owed"),
  setServerMargin: (pct: number) => request<{ serverMarginPct: number }>("PUT", "/api/admin/server-margin", { serverMarginPct: pct }),
  addPayment: (userId: string, amountUsd: number, note: string) => post<OwedReport>("/api/admin/owed/payments", { userId, amountUsd, note }),
  settle: (userId: string) => post<OwedReport>(`/api/admin/owed/${userId}/settle`),
  deletePayment: (id: number) => request<OwedReport>("DELETE", `/api/admin/owed/payments/${id}`),
  setAccountFlags: (userId: string, flags: { isAdmin?: boolean; canHost?: boolean; canUseServerKey?: boolean }) =>
    request<Account>("PATCH", `/api/admin/accounts/${userId}`, flags),

  // sessions, memory, voices (Phase 3)
  sessions: (c: number) => getJson<SessionsResponse>(`/api/campaigns/${c}/sessions`),
  // Playtest & observation
  replay: (c: number, sid: number) => getJson<ReplayResponse>(`/api/campaigns/${c}/sessions/${sid}/replay`),
  playtest: (c: number, idleSec: number) => getJson<PlaytestResponse>(`/api/campaigns/${c}/playtest?idle=${idleSec}`),
  feedbackList: (c: number) => getJson<FeedbackResponse>(`/api/campaigns/${c}/feedback`),
  playtestStatus: (c: number) => getJson<PlaytestStatus>(`/api/campaigns/${c}/playtest/status`),
  acknowledgeReview: (c: number) => post<{ ok: true }>(`/api/campaigns/${c}/playtest/acknowledge`),
  sendFeedback: (c: number, sid: number, rating: number | null, note = "") => post<{ ok: true }>(`/api/campaigns/${c}/sessions/${sid}/feedback`, { rating, note }),
  addReplayNote: (c: number, sid: number, eventKey: string, text: string) => post<ReplayNote>(`/api/campaigns/${c}/sessions/${sid}/notes`, { eventKey, text }),
  markReplayNote: (c: number, id: number, promoted: "global" | "campaign" | "suggested") => request<{ ok: true }>("PATCH", `/api/campaigns/${c}/replay-notes/${id}`, { promoted }),
  deleteReplayNote: (c: number, id: number) => del<{ ok: true }>(`/api/campaigns/${c}/replay-notes/${id}`),
  modelCosts: (c: number) => getJson<ModelCostsResponse>(`/api/campaigns/${c}/models/costs`),
  setModels: (c: number, patch: { dmModel?: string; bgModel?: string }) => put<LatencySettings>(`/api/campaigns/${c}/models`, patch),
  setLatency: (c: number, patch: Partial<LatencySettings>) => put<LatencySettings>(`/api/campaigns/${c}/latency`, patch),
  startSession: (c: number) => post<{ session: GameSession; created: boolean }>(`/api/campaigns/${c}/sessions/start`),
  endSession: (c: number) => post<{ session: GameSession | null }>(`/api/campaigns/${c}/sessions/end`),
  editSession: (c: number, sid: number, patch: { recap?: string; summary?: string }) =>
    request<GameSession>("PATCH", `/api/campaigns/${c}/sessions/${sid}`, patch),
  rescribe: (c: number, sid: number) => post<GameSession>(`/api/campaigns/${c}/sessions/${sid}/rescribe`),
  memory: (c: number) => getJson<MemoryResponse>(`/api/campaigns/${c}/memory`),
  updateBible: (c: number, patch: Partial<CampaignBible>) => request<CampaignBible>("PATCH", `/api/campaigns/${c}/memory/bible`, patch),
  createEntity: (c: number, m: MemoryInput) => post<Memory>(`/api/campaigns/${c}/memory/entities`, m),
  updateEntity: (c: number, id: number, m: MemoryInput) => request<Memory>("PATCH", `/api/campaigns/${c}/memory/entities/${id}`, m),
  deleteEntity: (c: number, id: number) => del<{ ok: true }>(`/api/campaigns/${c}/memory/entities/${id}`),
  deleteCharacterNote: (c: number, id: number) => del<{ ok: true }>(`/api/campaigns/${c}/memory/notes/${id}`),
  suggestGlobal: (c: number, category: GlobalMemoryCategory, text: string) => post<{ ok: true }>(`/api/campaigns/${c}/memory/suggest`, { category, text }),
  globalMemory: () => getJson<GlobalMemoryResponse>("/api/admin/global-memory"),
  addGlobal: (category: GlobalMemoryCategory, text: string) => post<{ entries: GlobalMemoryEntry[] }>("/api/admin/global-memory", { category, text }),
  updateGlobal: (id: number, category: GlobalMemoryCategory, text: string) =>
    request<{ entries: GlobalMemoryEntry[] }>("PATCH", `/api/admin/global-memory/${id}`, { category, text }),
  deleteGlobal: (id: number) => del<{ entries: GlobalMemoryEntry[] }>(`/api/admin/global-memory/${id}`),
  reorderGlobal: (ids: number[]) => request<{ entries: GlobalMemoryEntry[] }>("PUT", "/api/admin/global-memory/order", { ids }),
  decideSuggestion: (id: number, approve: boolean) => post<GlobalMemoryResponse>(`/api/admin/global-memory/suggestions/${id}`, { approve }),
  sayTyped: (c: number, text: string, toDm: boolean) => post<{ ok: true; line: TranscriptLine; toDm: boolean }>(`/api/campaigns/${c}/transcript/say`, { text, toDm }),
  talkToDm: (c: number, text: string) => post<{ ok: true; line: TranscriptLine }>(`/api/campaigns/${c}/dm/talk`, { text }),
  dmGo: (c: number) => post<{ ok: true }>(`/api/campaigns/${c}/dm/go`),
  dmPtt: (c: number, held: boolean) => post<{ ok: true }>(`/api/campaigns/${c}/dm/ptt`, { held }),
  voicePreviewUrl: (voice: string) => `/api/tts/preview?voice=${encodeURIComponent(voice)}`,
  campaignGlobalMemory: (c: number) => getJson<{ entries: GlobalMemoryEntry[] }>(`/api/campaigns/${c}/global-memory`),

  // whispers, journal, Host panel (Phase 3 part 2)
  whispers: (c: number) => getJson<{ whispers: Whisper[]; dmCanReply: boolean }>(`/api/campaigns/${c}/whispers`),
  whisper: (c: number, text: string) => post<{ whisper: Whisper; replying: boolean }>(`/api/campaigns/${c}/whispers`, { text }),
  journal: (c: number) => getJson<{ entries: JournalEntry[] }>(`/api/campaigns/${c}/journal`),
  addJournal: (c: number, title: string, text: string) => post<JournalEntry>(`/api/campaigns/${c}/journal`, { title, text }),
  updateJournal: (c: number, id: number, title: string, text: string) =>
    request<JournalEntry>("PATCH", `/api/campaigns/${c}/journal/${id}`, { title, text }),
  deleteJournal: (c: number, id: number) => del<{ ok: true }>(`/api/campaigns/${c}/journal/${id}`),
  hostPanel: (c: number) => getJson<HostPanelResponse>(`/api/campaigns/${c}/host`),
  setDifficulty: (c: number, difficulty: Difficulty) => request<{ difficulty: Difficulty }>("PUT", `/api/campaigns/${c}/host/difficulty`, { difficulty }),
  setPaused: (c: number, paused: boolean) => request<{ paused: boolean }>("PUT", `/api/campaigns/${c}/host/pause`, { paused }),
  rewind: (c: number, turnId: number) => post<RewindResult>(`/api/campaigns/${c}/host/rewind`, { turnId }),
  ruling: (c: number, text: string, veto: boolean) =>
    post<{ ruling: HostRuling; rewind: RewindResult | null }>(`/api/campaigns/${c}/host/ruling`, { text, veto }),

  me: () => getJson<MeResponse>("/api/me"),
  /** `session`: a session id, "none" (outside any session) or null (everything). */
  transcript: (campaignId: number, after: number, session: number | "none" | null = null, limit = 200) =>
    getJson<TranscriptResponse>(`/api/campaigns/${campaignId}/transcript?after=${after}&limit=${limit}${session === null ? "" : `&session=${session}`}`),
  logout: () => fetch("/auth/logout", { method: "POST", credentials: "same-origin" }),

  // characters
  party: (c: number) => getJson<PartyResponse>(`/api/campaigns/${c}/characters`),
  myCharacters: (c: number) => getJson<MyCharactersResponse>(`/api/campaigns/${c}/characters/mine`),
  character: (c: number, id: number) => getJson<Character>(`/api/campaigns/${c}/characters/${id}`),
  createCharacter: (c: number, sheet: CharacterSheet) => post<Character>(`/api/campaigns/${c}/characters`, sheet),
  updateCharacter: (c: number, id: number, sheet: CharacterSheet) =>
    request<Character>("PUT", `/api/campaigns/${c}/characters/${id}`, sheet),
  patchCharacter: (c: number, id: number, patch: SheetPatch) =>
    request<Character>("PATCH", `/api/campaigns/${c}/characters/${id}`, patch),
  rest: (c: number, id: number, type: "short" | "long") => post<Character>(`/api/campaigns/${c}/characters/${id}/rest`, { type }),
  spendHitDie: (c: number, id: number, die: number) =>
    post<{ roll: DiceRoll; character: Character }>(`/api/campaigns/${c}/characters/${id}/hit-dice`, { die }),
  preferences: () => getJson<{ preferences: UserPreferences }>("/api/me/preferences"),
  setSheetPrefs: (prefs: SheetPrefs) => request<{ preferences: UserPreferences }>("PUT", "/api/me/preferences/sheet", prefs),
  setCharacterStatus: (c: number, id: number, status: "active" | "retired") =>
    post<Character>(`/api/campaigns/${c}/characters/${id}/status`, { status }),
  characterHp: (c: number, id: number, kind: HpChangeKind, amount: number) =>
    post<DmActionResult>(`/api/campaigns/${c}/characters/${id}/hp`, { kind, amount }),
  deathSave: (c: number, id: number) =>
    post<{ roll: DiceRoll; message: string }>(`/api/campaigns/${c}/characters/${id}/death-save`),

  // dice
  rolls: (c: number) => getJson<{ rolls: DiceRoll[] }>(`/api/campaigns/${c}/rolls?limit=60`),
  roll: (c: number, input: RollInput) => post<DiceRoll>(`/api/campaigns/${c}/rolls`, input),
  rollRequests: (c: number) => getJson<{ requests: RollRequest[] }>(`/api/campaigns/${c}/roll-requests`),
  dismissRequest: (c: number, id: number) => post<{ ok: true }>(`/api/campaigns/${c}/roll-requests/${id}/dismiss`),

  // combat
  combat: (c: number) => getJson<CombatResponse>(`/api/campaigns/${c}/combat`),
  combatAction: (c: number, action: "start" | "next" | "end" | "roll-initiative" | "add-party") =>
    post<CombatResult>(`/api/campaigns/${c}/combat/${action}`),
  addMonsters: (
    c: number,
    m: { name: string; hp: number; ac: number; count: number; initiative: number | null; initBonus: number },
  ) => post<CombatResult>(`/api/campaigns/${c}/combat/monsters`, m),
  setInitiative: (c: number, cbId: number, value: number | null) =>
    post<CombatResponse>(`/api/campaigns/${c}/combat/combatants/${cbId}/initiative`, { value }),
  combatantHp: (c: number, cbId: number, kind: HpChangeKind, amount: number) =>
    post<CombatResult>(`/api/campaigns/${c}/combat/combatants/${cbId}/hp`, { kind, amount }),
  removeCombatant: (c: number, cbId: number) =>
    request<CombatResponse>("DELETE", `/api/campaigns/${c}/combat/combatants/${cbId}`),

  // Phase 5: rewards, level-up, loot, auto-roll, appeals
  awardXp: (c: number, amount: number, reason: string, each = false) => post<DmActionResult>(`/api/campaigns/${c}/awards/xp`, { amount, reason, each }),
  awardMilestone: (c: number, reason: string) => post<DmActionResult>(`/api/campaigns/${c}/awards/milestone`, { reason }),
  awardGold: (c: number, coins: { pp?: number; gp?: number; sp?: number; cp?: number }, reason: string) =>
    post<DmActionResult>(`/api/campaigns/${c}/awards/gold`, { ...coins, reason }),
  levelUp: (c: number, id: number, input: LevelUpInput) =>
    post<{ character: Character; summary: string; hpRoll: number | null }>(`/api/campaigns/${c}/characters/${id}/level-up`, input),
  levelHpRoll: (c: number, id: number, classId: string) =>
    post<{ roll: number; die: number; total: number }>(`/api/campaigns/${c}/characters/${id}/level-up/hp-roll`, { classId }),
  undoLevelUp: (c: number, id: number) => post<{ character: Character; summary: string }>(`/api/campaigns/${c}/characters/${id}/level-up/undo`, {}),
  levelGuide: (c: number, id: number, className: string) => post<{ text: string }>(`/api/campaigns/${c}/characters/${id}/level-guide`, { className }),
  loot: (c: number) => getJson<LootResponse>(`/api/campaigns/${c}/loot`),
  offerLoot: (c: number, items: Array<{ name: string; qty?: number; notes?: string }>) => post<LootResponse>(`/api/campaigns/${c}/loot`, { items }),
  claimLoot: (c: number, id: number, choice: LootChoice) => post<LootResponse>(`/api/campaigns/${c}/loot/${id}/claim`, { choice }),
  hostLoot: (c: number, id: number, op: "roll" | "cancel" | "give", characterId?: number) =>
    post<LootResponse>(`/api/campaigns/${c}/loot/${id}/${op}`, characterId ? { characterId } : {}),
  tableSettings: (c: number) => getJson<MyTableSettings>(`/api/campaigns/${c}/me/table-settings`),
  setTableSettings: (c: number, s: MyTableSettings) => request<MyTableSettings>("PUT", `/api/campaigns/${c}/me/table-settings`, s),
  appeals: (c: number) => getJson<{ appeals: Appeal[] }>(`/api/campaigns/${c}/appeals`),
  appeal: (c: number, text: string, lineId: number | null) => post<Appeal>(`/api/campaigns/${c}/appeals`, { text, lineId }),
  decideAppeal: (c: number, id: number, decision: "uphold" | "overturn", response: string) =>
    post<Appeal>(`/api/campaigns/${c}/appeals/${id}/decide`, { decision, response }),
  // Phase 6: help assistant
  help: (b: HelpAskInput) => post<HelpAnswer>("/api/help", b),
  // Phase 6: adventures
  adventures: () => getJson<AdventuresResponse>("/api/adventures"),
  adventure: (id: number) => getJson<Adventure>(`/api/adventures/${id}`),
  createAdventure: (title: string, content: Partial<AdventureContent>) => post<Adventure>("/api/adventures", { title, content }),
  saveAdventure: (id: number, body: { title?: string; content?: AdventureContent; shared?: boolean }) => put<Adventure>(`/api/adventures/${id}`, body),
  deleteAdventure: (id: number) => request<{ ok: true }>("DELETE", `/api/adventures/${id}`),
  copyAdventure: (id: number) => post<Adventure>(`/api/adventures/${id}/copy`),
  buildAdventure: (b: BuildAdventureInput) => post<Adventure>("/api/adventures/build", b),
  campaignAdventure: (c: number) => getJson<CampaignAdventureResponse>(`/api/campaigns/${c}/adventure`),
  startAdventure: (c: number, adventureId: number) => post<CampaignAdventureResponse>(`/api/campaigns/${c}/adventure`, { adventureId }),
  patchAdventure: (c: number, body: { currentAct?: number; title?: string; content?: AdventureContent; beat?: number; beatDone?: boolean }) =>
    request<CampaignAdventureResponse>("PATCH", `/api/campaigns/${c}/adventure`, body),
  suggestBeats: (c: number) => post<CampaignAdventureResponse>(`/api/campaigns/${c}/adventure/beats`),
  finishAdventure: (c: number) => post<CampaignAdventureResponse>(`/api/campaigns/${c}/adventure/finish`),

  // Session scheduling
  schedule: (c: number) => getJson<ScheduleResponse>(`/api/campaigns/${c}/schedule`),
  scheduleSettings: (c: number, b: Partial<ScheduleSettings>) => put<ScheduleResponse>(`/api/campaigns/${c}/schedule/settings`, b),
  addScheduleRule: (c: number, b: ScheduleRuleInput) => post<ScheduleResponse>(`/api/campaigns/${c}/schedule/rules`, b),
  deleteScheduleRule: (c: number, rid: number) => request<ScheduleResponse>("DELETE", `/api/campaigns/${c}/schedule/rules/${rid}`),
  skipSession: (c: number, rid: number, date: string, undo = false) => post<ScheduleResponse>(`/api/campaigns/${c}/schedule/rules/${rid}/skip`, { date, undo }),
  moveSession: (c: number, rid: number, date: string, newAt: string) => post<ScheduleResponse>(`/api/campaigns/${c}/schedule/rules/${rid}/move`, { date, newAt }),
  sessionIcsUrl: (c: number, rid: number, date: string) => `/api/campaigns/${c}/schedule/rules/${rid}/ics?date=${date}`,
  upcoming: () => getJson<UpcomingResponse>("/api/me/upcoming"),
  resetCalendar: () => post<UpcomingResponse>("/api/me/calendar/reset"),

  // Phase 6.3: scene images and ambient music
  images: (c: number) => getJson<ImagesResponse>(`/api/campaigns/${c}/images`),
  setMainImage: (c: number, id: number) => post<CampaignImage>(`/api/campaigns/${c}/images/${id}/main`, {}),
  newEntryPicture: (c: number, memoryId: number, b: { note?: string; show?: boolean }) =>
    post<CampaignImage>(`/api/campaigns/${c}/memories/${memoryId}/picture`, b),
  createImage: (c: number, b: CreateImageInput) => post<CampaignImage>(`/api/campaigns/${c}/images`, b),
  updateImage: (c: number, id: number, b: { shown?: boolean; caption?: string; memoryId?: number | null }) =>
    request<CampaignImage>("PATCH", `/api/campaigns/${c}/images/${id}`, b),
  deleteImage: (c: number, id: number) => request<{ ok: true }>("DELETE", `/api/campaigns/${c}/images/${id}`),
  imageSettings: (c: number, b: { dmImages?: boolean; dmImagesPerSession?: number }) => put<ImagesResponse>(`/api/campaigns/${c}/image-settings`, b),
  setMood: (c: number, mood: SceneMood) => post<{ mood: SceneMood }>(`/api/campaigns/${c}/mood`, { mood }),

  // Phase 5.2: homebrew, wiki, Copy DM
  homebrew: (c: number) => getJson<HomebrewResponse>(`/api/campaigns/${c}/homebrew`),
  addHomebrew: (c: number, h: HomebrewInput) => post<Homebrew>(`/api/campaigns/${c}/homebrew`, h),
  updateHomebrew: (c: number, id: number, h: Partial<HomebrewInput>) => request<Homebrew>("PATCH", `/api/campaigns/${c}/homebrew/${id}`, h),
  deleteHomebrew: (c: number, id: number) => request<{ ok: true }>("DELETE", `/api/campaigns/${c}/homebrew/${id}`),
  decideHomebrew: (c: number, id: number, approve: boolean) => post<Homebrew>(`/api/campaigns/${c}/homebrew/${id}/decide`, { approve }),
  shareHomebrew: (c: number, id: number) => post<Homebrew>(`/api/campaigns/${c}/homebrew/${id}/share`),
  adminHomebrew: () => getJson<AdminHomebrewResponse>("/api/admin/homebrew"),
  adminAddHomebrew: (h: HomebrewInput) => post<Homebrew>("/api/admin/homebrew", h),
  adminUpdateHomebrew: (id: number, h: Partial<HomebrewInput>) => request<Homebrew>("PATCH", `/api/admin/homebrew/${id}`, h),
  adminDeleteHomebrew: (id: number) => request<{ ok: true }>("DELETE", `/api/admin/homebrew/${id}`),
  adminDecideHomebrew: (id: number, approve: boolean) => post<Homebrew>(`/api/admin/homebrew/${id}/decide`, { approve }),
  wiki: (c: number) => getJson<WikiIndex>(`/api/campaigns/${c}/wiki`),
  wikiPage: (c: number, id: number) => getJson<WikiPage>(`/api/campaigns/${c}/wiki/${id}`),
  // Phase 5.3: voices, import
  elevenVoices: (c: number, fresh = false) => getJson<{ voices: ElevenVoice[] }>(`/api/campaigns/${c}/elevenlabs/voices${fresh ? "?fresh=1" : ""}`),
  setVoiceSettings: (c: number, s: VoiceSettingsInput) => request<Campaign>("PUT", `/api/campaigns/${c}/voice-settings`, s),
  setAvatar: (c: number, id: number, data: string) => put<Character>(`/api/campaigns/${c}/characters/${id}/avatar`, { data }),
  removeAvatar: (c: number, id: number) => del<Character>(`/api/campaigns/${c}/characters/${id}/avatar`),
  importCharacter: (c: number, file: { fileName: string; mime: string; data: string }) => post<CharacterImport>(`/api/campaigns/${c}/characters/import`, file),
  copyDmSources: (c: number) => getJson<{ sources: CopyDmSource[] }>(`/api/campaigns/${c}/copy-dm`),
  copyDm: (c: number, fromId: number) => post<Campaign>(`/api/campaigns/${c}/copy-dm`, { fromId }),
  withdrawAppeal: (c: number, id: number) => post<Appeal>(`/api/campaigns/${c}/appeals/${id}/withdraw`),
};
